import React, { useState } from 'react';
import {
  TextField, InputAdornment, Box, Paper, List, ListItem, ListItemAvatar,
  Avatar, ListItemText, Typography, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Grid, Divider
} from '@mui/material';
import { Search as SearchIcon, Person as PersonIcon, Warning as WarningIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { academyStore } from '../services/academyStore';
import { Student } from '../types/academy';
import { useSystemParameters } from '../../Commun/contexts/SystemParametersContext';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

export const GlobalStudentSearch: React.FC = () => {
  const { t } = useLanguage();
  const { getParam } = useSystemParameters();
  const currencySymbol = getParam('Currency_Symbol', 'DZD');
  const [query, setQuery] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);

  const hasQuery = query.trim().length > 0;
  const needsData = hasQuery || !!selectedStudent;
  const students = needsData ? academyStore.getStudents() : [];
  const invoices = needsData ? academyStore.getInvoices() : [];

  const filteredStudents = hasQuery
    ? students.filter(s =>
        `${s.first_name} ${s.last_name}`.toLowerCase().includes(query.toLowerCase()) ||
        (s.email ?? '').toLowerCase().includes(query.toLowerCase())
      )
    : [];

  const getStudentFinancialSnapshot = (studentId: number) => {
    const studentInvoices = invoices.filter(i => i.student_id === studentId);
    const totalInvoiced = studentInvoices.reduce((acc, i) => acc + i.total_amount, 0);
    const totalPaid = studentInvoices.reduce((acc, i) => acc + i.amount_paid, 0);
    const restToPay = totalInvoiced - totalPaid;
    return { totalInvoiced, totalPaid, restToPay };
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: 450 }}>
      <TextField
        fullWidth
        size="small"
        placeholder={t('academy.search.placeholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'primary.main' }} />
            </InputAdornment>
          ),
          sx: {
            borderRadius: 3,
            backgroundColor: 'background.paper',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            '& fieldset': { borderColor: 'rgba(99, 102, 241, 0.2)' },
            '&:hover fieldset': { borderColor: 'primary.main' },
          }
        }}
      />

      {/* Instant Floating Search Results */}
      {filteredStudents.length > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 1,
            zIndex: 1400,
            maxHeight: 320,
            overflowY: 'auto',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <List disablePadding>
            {filteredStudents.map(student => {
              const { restToPay } = getStudentFinancialSnapshot(student.id);
              return (
                <ListItem
                  key={student.id}
                  button
                  onClick={() => {
                    setSelectedStudent(student);
                    setQuery('');
                  }}
                  sx={{
                    '&:hover': { backgroundColor: 'action.hover' },
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: student.financial_hold ? 'error.light' : 'primary.light' }}>
                      {student.first_name[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {student.first_name} {student.last_name}
                        </Typography>
                        {student.financial_hold && (
                          <Chip label={t('academy.students.hold_badge')} size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                    }
                    secondary={student.email}
                  />
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color={restToPay > 0 ? 'error.main' : 'success.main'} fontWeight="bold">
                      {t('academy.search.debt')} {currencySymbol}{restToPay}
                    </Typography>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </Paper>
      )}

      {/* Student Profile Quick View Dialog */}
      {selectedStudent && (
        <Dialog open={!!selectedStudent} onClose={() => setSelectedStudent(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
            <Avatar sx={{ bgcolor: selectedStudent.financial_hold ? 'error.main' : 'primary.main', width: 44, height: 44 }}>
              {selectedStudent.first_name[0]}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {selectedStudent.first_name} {selectedStudent.last_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedStudent.email} • {selectedStudent.phone}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">{t('academy.search.dob')}</Typography>
                <Typography variant="body2" fontWeight="medium">{selectedStudent.date_of_birth}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">{t('academy.search.emergency_contact')}</Typography>
                <Typography variant="body2" fontWeight="medium">{selectedStudent.emergency_contact}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              {t('academy.search.financial_overview')}
            </Typography>
            {(() => {
              const { totalInvoiced, totalPaid, restToPay } = getStudentFinancialSnapshot(selectedStudent.id);
              return (
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">{t('academy.search.invoiced')}</Typography>
                      <Typography variant="h6" fontWeight="bold">{currencySymbol}{totalInvoiced}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderColor: 'success.light' }}>
                      <Typography variant="caption" color="success.main">{t('academy.search.total_paid')}</Typography>
                      <Typography variant="h6" color="success.main" fontWeight="bold">{currencySymbol}{totalPaid}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderColor: restToPay > 0 ? 'error.light' : 'divider' }}>
                      <Typography variant="caption" color={restToPay > 0 ? 'error.main' : 'text.secondary'}>{t('academy.search.rest_to_pay')}</Typography>
                      <Typography variant="h6" color={restToPay > 0 ? 'error.main' : 'text.primary'} fontWeight="bold">{currencySymbol}{restToPay}</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              );
            })()}

            {selectedStudent.financial_hold && (
              <Box sx={{ mt: 2.5, p: 1.5, borderRadius: 2, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <WarningIcon color="error" />
                <Box>
                  <Typography variant="subtitle2" color="error.main" fontWeight="bold">{t('academy.search.hold_active')}</Typography>
                  <Typography variant="caption" color="error.dark">
                    {t('academy.search.hold_desc')}
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedStudent(null)} variant="outlined">{t('btn.close')}</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};
